/**
 * Cognito Hosted UI sign-in (authorization code + PKCE) via oidc-client-ts.
 * Tokens live in sessionStorage and are never sent anywhere but our API.
 */
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import type { AdminConfig } from "./config";

export type Auth = {
  /** Resolves to a bearer token, or undefined when auth is disabled (local dev). */
  getAccessToken(): Promise<string | undefined>;
  userLabel(): string | undefined;
  signOut(): Promise<void>;
};

/** Completes a sign-in redirect if present, then ensures a signed-in user. Redirects away if not. */
export async function initAuth(config: AdminConfig["auth"]): Promise<Auth | "redirecting"> {
  if (config.mode === "none") {
    return { getAccessToken: async () => undefined, userLabel: () => "local (no auth)", signOut: async () => {} };
  }

  const appUrl = `${window.location.origin}/`;
  const manager = new UserManager({
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: appUrl,
    response_type: "code",
    scope: "openid email profile",
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
  });

  const params = new URLSearchParams(window.location.search);
  if (params.has("code") && params.has("state")) {
    const user = await manager.signinRedirectCallback();
    const returnTo = typeof user.state === "string" && user.state.startsWith("#") ? user.state : "#/works";
    window.history.replaceState(null, "", `/${returnTo}`);
  }

  let user = await manager.getUser();
  if (!user || user.expired) {
    try {
      user = user?.refresh_token ? await manager.signinSilent() : null;
    } catch {
      user = null;
    }
  }
  if (!user || user.expired) {
    await manager.signinRedirect({ state: window.location.hash || "#/works" });
    return "redirecting";
  }

  return {
    async getAccessToken() {
      let current = await manager.getUser();
      if (!current || current.expired) current = await manager.signinSilent();
      if (!current) throw new Error("not signed in");
      return current.access_token;
    },
    userLabel: () => (user.profile.email as string | undefined) ?? user.profile.sub,
    async signOut() {
      await manager.removeUser();
      // Cognito has no OIDC end_session endpoint; use its logout URL.
      const logout = new URL("/logout", config.domain);
      logout.searchParams.set("client_id", config.clientId);
      logout.searchParams.set("logout_uri", appUrl);
      window.location.assign(logout.toString());
    },
  };
}
